import React from 'react';
import { useTranslation } from 'react-i18next';
import { createWhatsAppChannelAccount } from '../services/channelAccountService';
import { ToggleField } from '../../../shared/components';
import styles from './ConnectorsPage.module.css';

interface AddWhatsAppAccountFormProps {
  canManage: boolean;
  onCreated: () => void;
  onCancel: () => void;
}

const emptyForm = {
  displayName: '',
  cloudPhoneNumberId: '',
  phoneNumber: '',
  whatsAppAccountId: '',
  cloudAccessToken: '',
  cloudApiBaseUrl: 'https://graph.facebook.com',
  cloudApiVersion: 'v25.0',
  cloudWebhookVerifyToken: '',
  cloudAppSecret: '',
  validateCloudSignature: false,
  isDefault: false,
};

export const AddWhatsAppAccountForm: React.FC<AddWhatsAppAccountFormProps> = ({
  canManage,
  onCreated,
  onCancel,
}) => {
  const { t } = useTranslation(['companies', 'admin']);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = async () => {
    if (!form.displayName.trim() || !form.cloudPhoneNumberId.trim() || !form.cloudAccessToken.trim()) {
      setError(t('channelAccountAddRequiredFields', { ns: 'companies' }));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createWhatsAppChannelAccount({
        displayName: form.displayName.trim(),
        cloudPhoneNumberId: form.cloudPhoneNumberId.trim(),
        phoneNumber: form.phoneNumber.trim() || undefined,
        whatsAppAccountId: form.whatsAppAccountId.trim() || undefined,
        cloudAccessToken: form.cloudAccessToken.trim(),
        cloudApiBaseUrl: form.cloudApiBaseUrl.trim() || undefined,
        cloudApiVersion: form.cloudApiVersion.trim() || undefined,
        cloudWebhookVerifyToken: form.cloudWebhookVerifyToken.trim() || undefined,
        cloudAppSecret: form.cloudAppSecret.trim() || undefined,
        validateCloudSignature: form.validateCloudSignature,
        isDefault: form.isDefault,
      });
      setForm(emptyForm);
      onCreated();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToCreateChannelAccount', { ns: 'companies' }));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return null;
  }

  return (
    <div className={styles.channelAccountAddForm}>
      <h4>{t('channelAccountAddWhatsAppHeading', { ns: 'companies' })}</h4>
      <p style={{ color: '#516173', marginTop: 0 }}>
        {t('channelAccountAddWhatsAppHelp', { ns: 'companies' })}
      </p>

      {error ? <div className="error">{error}</div> : null}

      <div className="company-form-grid">
        <label>
          {t('channelAccountDisplayName', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.displayName}
            onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))}
            placeholder={t('channelAccountDisplayNamePlaceholder', { ns: 'companies' })}
          />
        </label>

        <label>
          {t('whatsappCloudPhoneNumberId', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.cloudPhoneNumberId}
            onChange={event => setForm(prev => ({ ...prev, cloudPhoneNumberId: event.target.value }))}
          />
        </label>

        <label>
          {t('whatsappPhoneNumber', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.phoneNumber}
            onChange={event => setForm(prev => ({ ...prev, phoneNumber: event.target.value }))}
          />
        </label>

        <label>
          {t('whatsappAccountId', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.whatsAppAccountId}
            onChange={event => setForm(prev => ({ ...prev, whatsAppAccountId: event.target.value }))}
          />
        </label>

        <label>
          {t('whatsappAccessToken', { ns: 'companies' })}
          <input
            className="text-input"
            type="password"
            value={form.cloudAccessToken}
            onChange={event => setForm(prev => ({ ...prev, cloudAccessToken: event.target.value }))}
            autoComplete="new-password"
          />
        </label>

        <label>
          {t('whatsappWebhookVerifyToken', { ns: 'companies' })}
          <input
            className="text-input"
            type="password"
            value={form.cloudWebhookVerifyToken}
            onChange={event => setForm(prev => ({ ...prev, cloudWebhookVerifyToken: event.target.value }))}
            autoComplete="new-password"
          />
        </label>

        <label>
          {t('whatsappAppSecret', { ns: 'companies' })}
          <input
            className="text-input"
            type="password"
            value={form.cloudAppSecret}
            onChange={event => setForm(prev => ({ ...prev, cloudAppSecret: event.target.value }))}
            autoComplete="new-password"
          />
        </label>

        <ToggleField
          label={t('channelAccountDefaultLabel', { ns: 'companies' })}
          checked={form.isDefault}
          onChange={value => setForm(prev => ({ ...prev, isDefault: value }))}
        />
      </div>

      <div className="company-actions-inline" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={saving}>
          {saving ? t('savingEllipsis', { ns: 'admin' }) : t('channelAccountCreate', { ns: 'companies' })}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          {t('channelAccountCancelAdd', { ns: 'companies' })}
        </button>
      </div>
    </div>
  );
};
